/**
 * Memory Card Game
 * Flip cards to find matching pairs
 */
import { useState, useEffect } from 'react';
import { VocabularyWord } from '../../data/vocabulary';

interface MemoryGameProps {
  words: VocabularyWord[];
  onComplete: (score: number) => void;
  onExit: () => void;
}

interface Card {
  id: string;
  content: string;
  type: 'word' | 'meaning' | 'image';
  pairId: string;
  isFlipped: boolean;
  isMatched: boolean;
}

export default function MemoryGame({ words, onComplete, onExit }: MemoryGameProps) {
  const [cards, setCards] = useState<Card[]>([]);
  const [flippedCards, setFlippedCards] = useState<string[]>([]);
  const [matches, setMatches] = useState(0);
  const [score, setScore] = useState(0);
  const [moves, setMoves] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [showMatch, setShowMatch] = useState(false);

  // Initialize game
  useEffect(() => {
    initGame();
  }, [words]);

  const initGame = () => {
    const gameWords = words.slice(0, 6); // 6 words = 12 cards
    const cardsData: Card[] = [];

    gameWords.forEach((word, idx) => {
      // Word card
      cardsData.push({
        id: `word-${idx}`,
        content: word.word,
        type: 'word',
        pairId: word.id,
        isFlipped: false,
        isMatched: false,
      });

      // Meaning/Image card
      cardsData.push({
        id: `meaning-${idx}`,
        content: word.image || word.meaning,
        type: word.image ? 'image' : 'meaning',
        pairId: word.id,
        isFlipped: false,
        isMatched: false,
      });
    });

    // Shuffle
    const shuffled = cardsData.sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setMatches(0);
    setScore(0);
    setMoves(0);
    setFlippedCards([]);
  };

  const handleCardClick = (cardId: string) => {
    if (isLocked) return;

    const card = cards.find(c => c.id === cardId);
    if (!card || card.isFlipped || card.isMatched) return;

    // Flip the card
    const newCards = cards.map(c =>
      c.id === cardId ? { ...c, isFlipped: true } : c
    );
    setCards(newCards);

    const newFlipped = [...flippedCards, cardId];
    setFlippedCards(newFlipped);

    if (newFlipped.length === 2) {
      setIsLocked(true);
      setMoves(m => m + 1);

      const [firstId, secondId] = newFlipped;
      const firstCard = newCards.find(c => c.id === firstId);
      const secondCard = newCards.find(c => c.id === secondId);

      if (firstCard && secondCard && firstCard.pairId === secondCard.pairId) {
        // Match!
        setTimeout(() => {
          setCards(prev => prev.map(c =>
            c.id === firstId || c.id === secondId
              ? { ...c, isMatched: true }
              : c
          ));
          setMatches(m => m + 1);
          setScore(s => s + Math.max(20 - moves, 10));
          setFlippedCards([]);
          setIsLocked(false);
          setShowMatch(true);
          setTimeout(() => setShowMatch(false), 800);
        }, 600);
      } else {
        // No match
        setTimeout(() => {
          setCards(prev => prev.map(c =>
            c.id === firstId || c.id === secondId
              ? { ...c, isFlipped: false }
              : c
          ));
          setFlippedCards([]);
          setIsLocked(false);
        }, 1000);
      }
    }
  };

  // Check game completion
  useEffect(() => {
    if (matches === cards.length / 2 && cards.length > 0) {
      const finalScore = score + Math.max(50 - moves * 2, 20);
      setTimeout(() => {
        onComplete(finalScore);
      }, 1000);
    }
  }, [matches, cards.length]);

  const totalPairs = cards.length / 2;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <button onClick={onExit} className="text-gray-500 hover:text-gray-700">
          ← 返回
        </button>
        <div className="text-center">
          <h2 className="text-xl font-bold text-orange-600">🎴 记忆翻牌</h2>
          <p className="text-sm text-gray-500">找出所有配对的卡片</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold text-orange-600">得分: {score}</p>
          <p className="text-xs text-gray-400">
            配对: {matches}/{totalPairs}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="flex justify-center gap-6 mb-6">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-700">{moves}</p>
          <p className="text-xs text-gray-500">翻牌次数</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-orange-600">{matches}</p>
          <p className="text-xs text-gray-500">已配对</p>
        </div>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-4 gap-3">
        {cards.map(card => (
          <button
            key={card.id}
            onClick={() => handleCardClick(card.id)}
            disabled={card.isMatched || isLocked}
            className={`
              aspect-square rounded-xl transition-all duration-300 transform
              ${card.isMatched
                ? 'bg-green-100 scale-95 opacity-60 cursor-not-allowed'
                : card.isFlipped
                ? 'bg-orange-100 scale-105'
                : 'bg-gradient-to-br from-orange-400 to-pink-500 hover:scale-105 cursor-pointer shadow-lg'
              }
            `}
            style={{ perspective: '1000px' }}
          >
            {card.isMatched ? (
              <div className="h-full flex items-center justify-center text-4xl">
                ✓
              </div>
            ) : card.isFlipped ? (
              <div className="h-full flex items-center justify-center p-2">
                {card.type === 'image' ? (
                  <span className="text-3xl">{card.content}</span>
                ) : card.type === 'word' ? (
                  <span className="text-sm font-bold text-gray-800 text-center leading-tight">
                    {card.content}
                  </span>
                ) : (
                  <span className="text-xs text-gray-700 text-center leading-tight">
                    {card.content}
                  </span>
                )}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-4xl text-white">
                ❓
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Match celebration */}
      {showMatch && (
        <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-50">
          <div className="text-6xl animate-bounce-in">🎉</div>
        </div>
      )}

      {/* Completion screen */}
      {matches === totalPairs && totalPairs > 0 && (
        <div className="fixed inset-0 bg-white/90 flex items-center justify-center animate-bounce-in z-50">
          <div className="text-center">
            <div className="text-6xl mb-4">🏆</div>
            <h2 className="text-2xl font-bold text-orange-600 mb-2">太厉害了！</h2>
            <p className="text-lg text-gray-600 mb-1">总分: {score + Math.max(50 - moves * 2, 20)} 分</p>
            <p className="text-sm text-gray-400 mb-4">
              翻牌 {moves} 次，配对 {matches} 组
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={onExit}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300"
              >
                返回
              </button>
              <button
                onClick={initGame}
                className="px-6 py-3 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600"
              >
                再玩一次 🔄
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-400">
          💡 记住卡片位置，用最少的次数找到所有配对！
        </p>
      </div>
    </div>
  );
}